import { useEffect, useState } from "react";
import { ApiError, api, type CasoDetalle as CasoT, type CasoModeloItem } from "@/lib/api";
import Modal from "@/ui/Modal";
import MarcaModeloMultiPicker, { type ModeloItem } from "@/components/MarcaModeloMultiPicker";
import { useToast } from "@/ui/Toast";

type Props = {
  open: boolean;
  onClose: () => void;
  caso: CasoT;
  onSaved: (updated: CasoT) => void;
};

/** Convierte CasoModeloItem[] → ModeloItem[] (el formato que espera MarcaModeloMultiPicker). */
function toPickerItems(modelos: CasoModeloItem[]): ModeloItem[] {
  return modelos.map((m) => ({ marca: m.marca ?? "", modelo: m.modelo }));
}

/**
 * Modal para editar la lista de modelos de un caso (admin/soporte).
 * Llama a PUT /api/casos/{id}/modelos con la lista resultante.
 * Anti-cierre: solo se cierra con Escape, botón Cancelar o botón Guardar.
 */
export default function EditarModelosModal({ open, onClose, caso, onSaved }: Props) {
  const toast = useToast();
  const [items, setItems] = useState<ModeloItem[]>([]);
  const [saving, setSaving] = useState(false);

  // Precargar con los modelos actuales al abrir el modal.
  useEffect(() => {
    if (open) {
      setItems(
        caso.modelos.length
          ? toPickerItems(caso.modelos)
          : [{ marca: caso.marca ?? "", modelo: caso.modelo ?? "" }],
      );
    }
  }, [open, caso]);

  async function guardar() {
    // Filtrar filas vacías antes de guardar.
    const validos = items.filter((it) => it.modelo.trim() !== "");
    if (!validos.length) {
      toast.error("Ingresa al menos un modelo");
      return;
    }
    setSaving(true);
    try {
      const updated = await api.put<CasoT>(`/api/casos/${caso.id}/modelos`, {
        modelos: validos,
      });
      toast.success("Modelos actualizados");
      onSaved(updated);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error al guardar";
      toast.error("No se pudo guardar", msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => { if (!v && !saving) onClose(); }}
      title="Editar modelos del caso"
      description="El primer modelo es el principal. Puedes agregar o quitar modelos del caso."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn btn-ghost text-sm"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={saving}
            className="btn bg-accent-600 hover:bg-accent-500 text-white text-sm"
          >
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </>
      }
    >
      <MarcaModeloMultiPicker value={items} onChange={setItems} />
    </Modal>
  );
}
